import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { categories, TOURNAMENT_RUN_OPTIONS } from 'constants/tests';
import { useQueryParams } from 'hooks/useQueryParams';
import { useFreecodeContextRun, useFreecodeContextData, useFreecodeContextLayout } from 'hooks/useContext';
import { tabLabels, identifiers, buttonIds, editorTypes } from '../../FreecodeLayout/FreecodeLayoutConstants';
import FreecodeTabSelector from 'components/FreecodePage/FreecodeTabSelector/FreecodeTabSelector';
import { OptionContainer } from 'components/FreecodePage/FreecodeTabSelector/styles';
import { FreecodeCheckbox } from 'components/Checkbox/Checkbox';
import { FreecodeButton } from 'components/Button/RectangleButton';
import validateTags from 'functions/validateTags';
import RectangleDropdownButton from 'components/Button/RectangleDropdownButton';
import FreecodeAuthModal from 'components/Modals/FreecodeModals/FreecodeAuthModal/FreecodeAuthModal';
import Console from './Console';
import Score from './Score';
import TagError from './TagError';
import YesNoModal from 'components/Modals/yesNoModal';
import LiveCodingStatus from './LiveCodingStatus';

const submitEditors = [editorTypes.contest, editorTypes.freecode, editorTypes.freecodeTrial];

export default function FreecodeConsoleAndScore() {
  const navigate = useNavigate();
  const { courseId } = useQueryParams();

  const { setDisplaySubmit, editorType } = useFreecodeContextLayout();
  const { test, testImpl } = useFreecodeContextData();
  const {
    runFunction,
    isRunning,
    runHidden,
    setRunHidden,
    saveOnRun,
    toggleSaveOnRun,
    scoreTabScore,
    runAgainst,
    setRunAgainst,
  } = useFreecodeContextRun();

  const [nextButtonSettings, setNextButtonSettings] = useState({});
  const [displayNextModal, setDisplayNextModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const { userId } = useSelector((state) => state.auth);

  const [items, setItems] = useState([]);

  const descriptionErrorState = useRef({});
  const flowErrorState = useRef({});

  useEffect(() => {
    const tabItemRef = [
      {
        icon: tabLabels[identifiers.console].icon,
        label: tabLabels[identifiers.console].label,
        tabId: tabLabels[identifiers.console].tabId,
        component: <Console runFunction={runFunction} nextButtonSettings={nextButtonSettings} />,
      },
      {
        icon: tabLabels[identifiers.score].icon,
        label: `${tabLabels[identifiers.score].label} ${scoreTabScore}`,
        tabId: tabLabels[identifiers.score].tabId,
        component: <Score />,
      },
    ];

    if (descriptionErrorState.current.isInvalid || flowErrorState.current.isInvalid) {
      tabItemRef.push({
        icon: tabLabels[identifiers.errorTab].icon,
        label: tabLabels[identifiers.errorTab].label,
        tabId: tabLabels[identifiers.errorTab].tabId,
        component: (
          <TagError
            descriptionTagError={descriptionErrorState.current.error ?? false}
            flowTagError={flowErrorState.current.error ?? false}
          />
        ),
      });
    }

    setItems(tabItemRef);
  }, [nextButtonSettings, runFunction, scoreTabScore]);

  const runCode = () => {
    // Show auth modal on the 6th run code if user is not logged in
    if (!userId && [editorTypes.exercise, editorTypes.contest].includes(editorType)) {
      let runCount = Number(localStorage.getItem('runCodeCount') ?? 0);
      if (runCount === 5) {
        setShowAuthModal(true);
        runCount = 0;
      } else {
        runCount += 1;
      }
      localStorage.setItem('runCodeCount', runCount);
    }
    runFunction && runFunction();
  };

  const submit = () => setDisplaySubmit(true);

  useEffect(() => {
    const validateDescription = validateTags(testImpl.candidateDescription, testImpl.excludedTags);
    descriptionErrorState.current = {
      isInvalid: !validateDescription.isValid,
      error: validateDescription.isValid ? null : validateDescription,
    };

    flowErrorState.current = { isInvalid: false, error: {} };

    // To handle condition where level tags not opened and gets error from backend
    const hasErrorInParsedFlow = typeof testImpl.parsedFlow === 'string';
    if (hasErrorInParsedFlow) {
      let match = testImpl.parsedFlow.match(/\d+/);
      match = parseInt(match[0], 10);
      flowErrorState.current = {
        error: {
          [match]: {
            [match]: { isValid: false, defaultError: [testImpl.parsedFlow] },
          },
        },
      };
    } else if (testImpl.isFlow) {
      for (const key in testImpl.parsedFlow) {
        testImpl.parsedFlow[key].forEach((level, index) => {
          const validateFlow = validateTags(level, testImpl.excludedTags);
          if (!validateFlow.isValid) {
            flowErrorState.current.isInvalid = true;
            if (!flowErrorState.current.error[Number(key) + 1]) flowErrorState.current.error[Number(key) + 1] = {};
            flowErrorState.current.error[Number(key) + 1][index + 1] = validateFlow;
          }
        });
      }
    }

    if (editorType === editorTypes.creator) {
      const errorTab = {
        icon: tabLabels[identifiers.errorTab].icon,
        label: tabLabels[identifiers.errorTab].label,
        tabId: tabLabels[identifiers.errorTab].tabId,
        component: (
          <TagError
            descriptionTagError={descriptionErrorState.current.error ?? false}
            flowTagError={flowErrorState.error ?? false}
          />
        ),
      };

      setItems((oldValue) => {
        if (descriptionErrorState.current.isInvalid || flowErrorState.current.isInvalid) {
          const hasErrorTab = oldValue.find((item) => item.key === 'error');
          if (!hasErrorTab) return [...oldValue, errorTab];
          return oldValue;
        } else {
          return oldValue.filter((item) => item.key !== 'error');
        }
      });
    }
  }, [testImpl, editorType]);

  useEffect(() => {
    if (editorType !== editorTypes.exercise) return;
    let link = '/exercises';
    let buttonText = 'More Exercises';
    let buttonTitle = 'Click here to return to the exercise index page';
    let modalTitle = 'More Exercises';
    let modalText = 'Do you want to return to the exercise index page?';
    if (courseId && test.nextExercise) {
      link = `/exercise/${test.nextExercise}?courseId=${courseId}`;
      buttonText = 'Next Exercise';
      buttonTitle = 'Click here to go to the next course module';
      modalTitle = 'Next Exercise';
      modalText = 'Do you want to continue to the next exercise that is part of the course?';
    } else if (courseId) {
      link = `/courses/${courseId}`;
      buttonText = 'Course Page';
      buttonTitle = 'Click here to go to the course landing page';
      modalTitle = 'Course Page';
      modalText = 'Do you want to return to the course page?';
    }
    setNextButtonSettings({ link, buttonText, buttonTitle, modalTitle, modalText, setDisplayNextModal });
  }, [courseId, editorType, test.nextExercise]);

  const getRunButton = () => {
    if (editorType === editorTypes.liveCoding) return <LiveCodingStatus />;
    if (editorType === editorTypes.contest && test.isTournament)
      return (
        <RectangleDropdownButton
          id={buttonIds.runButton}
          label="Run Code"
          options={TOURNAMENT_RUN_OPTIONS}
          value={runAgainst}
          disabled={isRunning}
          onClick={runCode}
          onChange={(response) => {
            setRunAgainst(response.value);
            localStorage.setItem('fsEditor-runAgainst', response.value);
          }}
        />
      );
    return (
      <FreecodeButton id={buttonIds.runButton} small onClick={runCode} disabled={isRunning}>
        Run Code
      </FreecodeButton>
    );
  };

  const getHeaderOptions = () => (
    <OptionContainer>
      {[editorTypes.freecodeInspect, editorTypes.creator, editorTypes.creatorInspect].includes(editorType) &&
        test.category !== categories.EXERCISE.category && (
          <FreecodeCheckbox checked={runHidden} label="Run Hidden Levels" onChange={() => setRunHidden(!runHidden)} />
        )}

      {editorType === editorTypes.creator && (
        <FreecodeCheckbox checked={saveOnRun} label="Save On Run" onChange={toggleSaveOnRun} />
      )}

      {getRunButton()}

      {submitEditors.includes(editorType) && (
        <FreecodeButton small onClick={submit} id={buttonIds.submitButton}>
          Submit
        </FreecodeButton>
      )}

      {displayNextModal && (
        <YesNoModal
          isFreecodeModal
          confirm={(yes) => {
            setDisplayNextModal(false);
            if (yes) navigate(nextButtonSettings.link);
          }}
          modalInfo={{
            title: nextButtonSettings.modalTitle,
            body: nextButtonSettings.modalText,
          }}
        />
      )}
    </OptionContainer>
  );

  return (
    <>
      <FreecodeTabSelector items={items} headerComponents={getHeaderOptions()} />
      {showAuthModal && <FreecodeAuthModal onCancel={() => setShowAuthModal(false)} />}
    </>
  );
}
